import {
  createSlice,
  createAsyncThunk,
  miniSerializeError,
  SerializedError,
} from "@reduxjs/toolkit";
import { Auth, CognitoUser } from "@aws-amplify/auth";
import { hasValidPermissions, LOADING_STATE } from "../constants/constants";
import { Init as initAuth } from "../config/Auth";
import authService from "src/services/authService";
import { authManager } from "src/services/authManager";
import { deviceDetectionService } from "src/services/deviceDetectionService";

export type UserState = {
  token: string;
  alias: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];
  hasPermission: boolean;
  pageLastLoadTimestamp: number;
  isLoading: boolean;
  siteLocation: string;
  
  // Loading states
  userLoading: LOADING_STATE;
  signInLoading: LOADING_STATE;
  signOutLoading: LOADING_STATE;
  initializeAuthLoading: LOADING_STATE;
  
  // Error states
  authError: SerializedError | null;
  signInError: SerializedError | null;
  signOutError: SerializedError | null;
  initializeAuthError: SerializedError | null;
};

const initialState: UserState = {
  token: "",
  alias: "",
  lastName: "",
  firstName: "",
  email: "",
  ldap: [],
  hasPermission: true,
  pageLastLoadTimestamp: 0,
  isLoading: false,
  siteLocation: "",
  
  // Initialize loading states
  userLoading: "pending",
  signInLoading: "idle",
  signOutLoading: "idle",
  initializeAuthLoading: "idle",
  
  // Initialize error states
  authError: null,
  signInError: null,
  signOutError: null,
  initializeAuthError: null,
};

/**
 * get the authenticated user
 * @returns {Object}
 */
export const getUser = createAsyncThunk("user/getUser", async () => {
  // Check device type to determine authentication method
  const deviceType = deviceDetectionService.getDeviceType();
  
  if (deviceType === 'mobile') {
    // For mobile devices, use the new auth manager
    const userContext = authManager.getCurrentUserContext();
    
    if (userContext) {
      // Convert to the format expected by the Redux store
      return {
        alias: userContext.userAlias,
        token: '', // We don't use a single token for federate auth
        email: userContext.email || '',
        firstName: userContext.firstName || '',
        lastName: userContext.lastName || '',
        siteLocation: userContext.siteLocation || '',
        ldap: userContext.ldapGroups || []
      };
    } else {
      // No valid authentication, throw error to trigger sign-in
      throw new Error('No valid authentication found');
    }
  } else {
    // For desktop devices, use existing Cognito authentication
    return await authService.login();
  }
});

/**
 * Sign in using appropriate authentication method based on device type
 */
export const signIn = createAsyncThunk("user/signIn", async () => {
  const deviceType = deviceDetectionService.getDeviceType();
  
  if (deviceType === 'mobile') {
    // For mobile devices, use device flow authentication
    const result = await authManager.authenticateUser();
    
    if (!result.success) {
      throw new Error(result.error || 'Mobile authentication failed');
    }
    
    return result;
  } else {
    // For desktop devices, use existing federated sign-in
    await authService.federatedSignIn();
  }
});

/**
 * Sign out the current user
 */
export const signOut = createAsyncThunk("user/signOut", async () => {
  const deviceType = deviceDetectionService.getDeviceType();
  
  if (deviceType === 'mobile') {
    // For mobile devices, use auth manager logout
    await authManager.logout();
  } else {
    // For desktop devices, use existing Cognito sign out
    await Auth.signOut();
  }
});

/**
 * Initialize auth and attempt to get the current user
 */
export const initializeAuth = createAsyncThunk(
  "user/initializeAuth",
  async (_, { dispatch }) => {
    try {
      const deviceType = deviceDetectionService.getDeviceType();
      console.log(`[initializeAuth] Device type detected: ${deviceType}`);
      
      if (deviceType === 'mobile') {
        // For mobile devices, initialize the new auth manager
        console.log('[initializeAuth] Initializing mobile auth manager');
        await authManager.initializeAuth();
        
        // Check if user is already authenticated
        const isAuthenticated = authManager.isAuthenticated();
        console.log(`[initializeAuth] Mobile authentication status: ${isAuthenticated}`);
        
        if (isAuthenticated) {
          try {
            console.log('[initializeAuth] Mobile user authenticated, getting user info');
            await dispatch(getUser()).unwrap();
            return true;
          } catch (error) {
            console.log("Mobile user not authenticated, will show sign-in option");
            return false;
          }
        } else {
          // Check if we should automatically redirect to device flow
          const currentPath = window.location.pathname;
          const isCallbackPage = currentPath === '/device_callback';
          
          // Don't auto-redirect if we're on the callback page - let it handle the callback first
          if (isCallbackPage) {
            console.log("On callback page, skipping auto-redirect to allow callback processing");
            return false;
          }
          
          // Auto-redirect if we're on a main page
          const shouldAutoRedirect = currentPath === '/' || currentPath === '/home' || currentPath.startsWith('/home');
          
          if (shouldAutoRedirect) {
            console.log("No mobile authentication found, automatically initiating device flow");
            
            try {
              await authManager.authenticateUser();
              // This will redirect to Federate, so we won't reach this point normally
              return true;
            } catch (error) {
              console.error("Failed to initiate device flow automatically:", error);
              // Fall back to showing sign-in button
              return false;
            }
          } else {
            console.log("No mobile authentication found, will show sign-in option");
            return false;
          }
        }
      } else {
        // For desktop devices, use existing Cognito initialization
        console.log('[initializeAuth] Initializing desktop Cognito auth');
        await initAuth();
        try {
          await dispatch(getUser()).unwrap();
          return true;
        } catch (error) {
          console.log("Desktop user not authenticated, will show sign-in option");
          return false;
        }
      }
    } catch (error) {
      console.error("Failed to initialize authentication:", error);
      throw error;
    }
  }
);

const { reducer, actions } = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.authError = null;
    },
    setPageLastLoadTimestamp: (state) => {
      state.pageLastLoadTimestamp = Date.now();
    },
  },
  extraReducers: (builder) => {
    // Get User
    builder.addCase(getUser.pending, (state) => {
      state.userLoading = "pending";
      state.isLoading = true;
      state.authError = null;
    });
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      const { alias, token, email, firstName, lastName, siteLocation, ldap} = payload;
      state.token = token;
      state.alias = alias;
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.ldap = ldap;
      state.userLoading = "fulfilled";
      state.isLoading = false;
      state.authError = null;
      state.siteLocation = siteLocation,
      state.hasPermission = hasValidPermissions(ldap);
      state.pageLastLoadTimestamp = Date.now();

      if (state.ldap.length === 0) {
        state.hasPermission = false;
      }
    });
    builder.addCase(getUser.rejected, (state, { error }) => {
      state.userLoading = "rejected";
      state.isLoading = false;
      state.authError = miniSerializeError(error);
      state.hasPermission = false;
      state.token = "";
      state.alias = "";
      state.email = "";
      state.firstName = "";
      state.lastName = "";
      state.siteLocation = "";
      state.ldap = [];
    });

    // Sign In
    builder.addCase(signIn.pending, (state) => {
      state.signInLoading = "pending";
      state.signInError = null;
      state.isLoading = true;
    });
    builder.addCase(signIn.fulfilled, (state) => {
      state.signInLoading = "fulfilled";
      state.isLoading = false;
    });
    builder.addCase(signIn.rejected, (state, { error }) => {
      state.signInLoading = "rejected";
      state.signInError = miniSerializeError(error);
      state.isLoading = false;
    });

    // Sign Out
    builder.addCase(signOut.pending, (state) => {
      state.signOutLoading = "pending";
      state.signOutError = null;
      state.isLoading = true;
    });
    builder.addCase(signOut.fulfilled, (state) => {
      state.signOutLoading = "fulfilled";
      state.userLoading = "pending";
      state.isLoading = false;
      state.authError = null;
      state.token = "";
      state.alias = "";
      state.email = "";
      state.firstName = "";
      state.lastName = "";
      state.ldap = [];
      state.hasPermission = true;
    });
    builder.addCase(signOut.rejected, (state, { error }) => {
      state.signOutLoading = "rejected";
      state.signOutError = miniSerializeError(error);
      state.isLoading = false;
    });

    // Initialize Auth
    builder.addCase(initializeAuth.pending, (state) => {
      state.initializeAuthLoading = "pending";
      state.initializeAuthError = null;
      state.isLoading = true;
    });
    builder.addCase(initializeAuth.fulfilled, (state, { payload }) => {
      // Always stop loading when auth initialization completes
      state.isLoading = false;
      
      // If payload is false, it means no authentication was found
      // Set userLoading to "rejected" to indicate user needs to sign in
      if (payload === false) {
        state.userLoading = "rejected";
        state.authError = null; // Clear any previous auth errors
      }
      // If payload is true, getUser was dispatched and will handle the state
    });
    builder.addCase(initializeAuth.rejected, (state, { error }) => {
      state.initializeAuthLoading = "rejected";
      state.initializeAuthError = miniSerializeError(error);
      state.isLoading = false;
    });
  },
});

export const { clearAuthError, setPageLastLoadTimestamp } = actions;

export default reducer;
